<script lang="ts">
	import { get } from 'svelte/store';
	// Formating
	import { format } from 'svelte-i18n';
	// Stores
	import { home_data } from 'src/stores/account/user';
	import { devices } from 'src/stores/lists/devices';
	// Helpers
	import { isPremiumUser } from 'src/helpers/user_helpers';
	import { getUserTimeZoneDate } from 'src/helpers/date_helper';
	import { capFirstChar } from 'src/helpers/utilities';
	// Design system components
	import {
		Toolbar,
		Breadcrumbs,
		Row,
		Col,
		ControlsContainer,
		Container,
	} from '@Shelly/shelly-design-system';
	// Components
	import ChartControls from '../../Chart/ChartJS/ChartControls.svelte';
	import EmptyList from 'src/components/Lists/EmptyList.svelte';
	import PVSetupCharts from './PVSetupCharts.svelte';
	import PVSetupEnergyDistribution from './PVSetupEnergyDistribution.svelte';
	import PVSetupPieChart from './PVSetupPieChart.svelte';
	import { getPVSetupDevices } from './pv_setup_helper';
	import { navigateToShop } from 'src/components/Lists/list-helpers';
	import PvSetupMode from './PVSetupMode.svelte';
	import EmptyPlaceholder from 'src/components/EmptyPlaceholder.svelte';
	// Types
	import type { ChartControlsDataInterface } from 'src/helpers/statistics/ui/chart_control_helpers';
	import type { PVSetupTypes } from './pv_setup_helper';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';

	const enabledHourControl = isPremiumUser();
	const PV_SETUP_TYPES: PVSetupTypes[] = ['Parallel', 'BackUP', 'BalconyPV'];
	const chartControlsEvent = (e: CustomEvent) => {
		chartControlsData = e.detail;
	};
	const onChangeEvent = (e: CustomEvent) => {
		if (e.detail.action == 'dblClick') {
			chartControlsElement.changeDateRangeOnClick(e);
		}
	};

	let chartControlsElement: ChartControls;
	let chartControlsData: ChartControlsDataInterface;
	let pvSetupType: PVSetupTypes = get(home_data)?.net_metering_type || PV_SETUP_TYPES[0];

	$: pvSetupDevices = $home_data && getPVSetupDevices(pvSetupType);
</script>

{#if Object.values($devices).length === 0}
	<div class="mt-md-m mt-sm-0 text-center d-flex flex-direction-column align-items-center">
		<EmptyPlaceholder class="mt-md-l mt-sm-s mb-m" />
		<EmptyList
			title={$format('no_power_measuring_device_title', {
				default: 'No power measuring devices.',
			})}
			description={$format('get_necessary_device_to_track_usage', {
				default: 'Get the necessary devices to start tracking energy usage.',
			})}
			btnText={capFirstChar(
				$format('button_go_to_store', {
					default: 'Go to store',
				}).toLocaleLowerCase(),
			)}
			onClick={() => {
				navigateToShop();
			}}
		/>
	</div>
{:else}
	<Container class="mb-s" backgroundColor={$showNewLayout ? 'color-base' : 'color-0'} noScroll>
		<PvSetupMode bind:pvSetupType {pvSetupDevices} />
		{#if pvSetupDevices?.gridDevices?.length && pvSetupDevices?.solarDevices?.length}
			<PVSetupEnergyDistribution {pvSetupType} />
		{/if}
	</Container>
	{#if pvSetupDevices?.gridDevices?.length && pvSetupDevices?.solarDevices?.length}
		<Container
			class="mb-s pt-m"
			backgroundColor={$showNewLayout ? 'color-base' : 'color-0'}
			noScroll
		>
			<Toolbar class="mb-s no-box-shadow">
				<Breadcrumbs
					aria-label={$format('breadcrumbs_energy_history', { default: 'Energy history' })}
					slot="breadcrumbs"
					links={[{ label: $format('tab_energy_item_history', { default: 'History' }) }]}
				/>
				<svelte:fragment slot="rightSide">
					<ChartControls
						bind:this={chartControlsElement}
						chartControlID="chart-overall-range-dp"
						overallPage={true}
						{enabledHourControl}
						timeZone={getUserTimeZoneDate()}
						on:init={chartControlsEvent}
						on:change={chartControlsEvent}
					/>
				</svelte:fragment>
			</Toolbar>
			{#if chartControlsData}
				<Row>
					<Col md="12" lg="8">
						<ControlsContainer
							orientation="vertical"
							color={$showNewLayout ? 'color-1' : undefined}
						>
							<PVSetupCharts
								slot="first"
								{chartControlsData}
								{pvSetupType}
								on:change={onChangeEvent}
							/>
						</ControlsContainer>
					</Col>
					<Col md="12" lg="4">
						<div class="grid">
							<ControlsContainer
								orientation="vertical"
								color={$showNewLayout ? 'color-1' : undefined}
							>
								<PVSetupPieChart slot="first" {chartControlsData} {pvSetupType} />
							</ControlsContainer>
						</div>
					</Col>
				</Row>
			{/if}
		</Container>
	{/if}
{/if}

<style>
	:global(.no-box-shadow) {
		box-shadow: none !important;
		background-color: transparent !important;
	}
	.grid {
		min-height: 100%;
		display: grid;
	}
</style>
